import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { SharedRecentSales } from '../../shared/SharedRecentSales/SharedRecentSales.tsx';
import SharedYMAL from '../../shared/SharedYMAL/SharedYMAL.tsx';
import { BuySharedPersonalizedSearchItems } from '../BuySharedPersonalizedSearchItems/BuySharedPersonalizedSearchItems.tsx';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { type BuySharedAsyncModules_viewer$key } from './__generated__/BuySharedAsyncModules_viewer.graphql.ts';
import { type BuySharedAsyncModules_buyPage$key } from './__generated__/BuySharedAsyncModules_buyPage.graphql.ts';
import { type BuySharedAsyncModules_user$key } from './__generated__/BuySharedAsyncModules_user.graphql.ts';

const viewerFragment = graphql`
    fragment BuySharedAsyncModules_viewer on Viewer {
        buyPageSoldResults(uriRef: $uriRef) @include(if: $afterDisplayOrBtSsr) {
            ...SharedRecentSales_buyPageSoldResults
        }
        buyPageItemRecommendations(
            uriRef: $uriRef
            topItemIds: $topItemIds
            categoryLevel: $categoryLevel
        ) @include(if: $isClient) {
            ...SharedYMAL_buyPageItemRecommendations
            ...BuySharedPersonalizedSearchItems_buyPageItemRecommendations
        }
        ...SharedRecentSales_viewer
        ...SharedYMAL_viewer
        ...BuySharedPersonalizedSearchItems_viewer
    }
`;

const buyPageFragment = graphql`
    fragment BuySharedAsyncModules_buyPage on ItemSearchQueryConnection {
        totalResults
        seoAbTestV2List {
            config {
                testName
            }
            variant
        }
        ...SharedRecentSales_itemSearch
        ...SharedYMAL_itemSearch
        ...BuySharedPersonalizedSearchItems_itemSearch
    }
`;

const userFragment = graphql`
    fragment BuySharedAsyncModules_user on User {
        ...SharedRecentSales_user
        ...SharedYMAL_user
        ...BuySharedPersonalizedSearchItems_user
    }
`;

type Props = {
    viewer: BuySharedAsyncModules_viewer$key;
    buyPage: BuySharedAsyncModules_buyPage$key;
    user: BuySharedAsyncModules_user$key;
    isMobile?: boolean;
};

export const BuySharedAsyncModules: FC<Props> = ({
    viewer: viewerRef,
    buyPage: buyPageRef,
    user: userRef,
    isMobile = false,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const buyPage = useFragment(buyPageFragment, buyPageRef);
    const user = useFragment(userFragment, userRef);
    const { isSsrOnlyBot } = useServerVarsContext();
    const { buyPageSoldResults, buyPageItemRecommendations } = viewer;
    const { totalResults, seoAbTestV2List } = buyPage;
    const isSsrBuyPageModulesVariant = seoAbTestV2List?.some(
        test => test?.config?.testName === 'ssr_buy_page_modules' && test?.variant === 'VARIANT1'
    );
    const isBuyPageSoldResultsLoading =
        !isSsrOnlyBot && isSsrBuyPageModulesVariant && typeof buyPageSoldResults === 'undefined';
    const isBuyPageItemRecommendationsLoading =
        !isSsrOnlyBot &&
        Number(totalResults) > 0 && // graphql gives us recommendations only for non-empty buy page
        isSsrBuyPageModulesVariant &&
        typeof buyPageItemRecommendations === 'undefined';
    return (
        <>
            <SharedRecentSales
                isMobile={isMobile}
                viewer={viewer}
                itemSearch={buyPage}
                buyPageSoldResults={buyPageSoldResults}
                isLoading={isBuyPageSoldResultsLoading}
                user={user}
                hideRecentSoldPrice={!user}
            />
            <SharedYMAL
                viewer={viewer}
                itemSearch={buyPage}
                buyPageItemRecommendations={buyPageItemRecommendations}
                isLoading={isBuyPageItemRecommendationsLoading}
                user={user}
            />
            <BuySharedPersonalizedSearchItems
                isMobile={isMobile}
                viewer={viewer}
                user={user}
                itemSearch={buyPage}
                buyPageItemRecommendations={buyPageItemRecommendations}
                isLoading={isBuyPageItemRecommendationsLoading}
            />
        </>
    );
};
